import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from '../../util/analyticsEvents';
import { SELLER_VIDEO } from '../../util/marketplaceVideos';
import { getModelThumbnailImageSrc } from '../../utils/modelImages';

import {
  H1,
  H2,
  IconCheckmark,
  LayoutSingleColumn,
  NamedLink,
  Page,
  VideoCard,
} from '../../components';
import useVideoPlayback from '../../components/VideoCard/useVideoPlayback';

import FooterContainer from '../FooterContainer/FooterContainer';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import {
  benefitIds,
  faqItems,
  heroOfferSteps,
  paymentFlowItems,
  reputationPointIds,
  saleSteps,
} from './SellingInfoPage.content';

import css from './SellingInfoPage.module.css';

const PREVIEW_IMAGE_SRC = getModelThumbnailImageSrc('/images/models/head-coello-pro-2025.png');

const StartSellingCTA = props => {
  const { className, children, ctaLocation } = props;
  return (
    <NamedLink
      name="NewListingPage"
      className={className}
      onClick={() =>
        trackAnalyticsEvent(ANALYTICS_EVENTS.SELL_CTA_CLICKED, {
          cta_location: ctaLocation,
          source_page: 'selling_info_page',
        })
      }
    >
      {children}
    </NamedLink>
  );
};

const SectionHeading = props => {
  const { id } = props;
  return (
    <H2 rootClassName={css.sectionHeading}>
      <FormattedMessage id={id} />
    </H2>
  );
};

/**
 * Informational landing page for sellers who want to sell a padel racket on RacketPort.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled
 * @returns {JSX.Element}
 */
export const SellingInfoPageComponent = props => {
  const { scrollingDisabled } = props;
  const intl = useIntl();
  const config = useConfiguration();
  const marketplaceName = config.marketplaceName;
  const { activeVideoId, onPlay, onClose } = useVideoPlayback();

  const title = intl.formatMessage({ id: 'SellingInfoPage.schemaTitle' }, { marketplaceName });
  const description = intl.formatMessage(
    { id: 'SellingInfoPage.schemaDescription' },
    { marketplaceName }
  );

  return (
    <Page title={title} description={description} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <section className={css.hero}>
            <div className={css.heroContent}>
              <p className={css.eyebrow}>
                <FormattedMessage id="SellingInfoPage.hero.eyebrow" />
              </p>
              <H1 rootClassName={css.heroTitle}>
                <FormattedMessage id="SellingInfoPage.hero.title" />
              </H1>
              <p className={css.heroText}>
                <FormattedMessage id="SellingInfoPage.hero.subtitle" />
              </p>
              <div className={css.heroActions}>
                <StartSellingCTA className={css.primaryButton} ctaLocation="selling_info_hero">
                  <FormattedMessage id="SellingInfoPage.cta.selectModel" />
                </StartSellingCTA>
                <a className={css.secondaryButton} href="#ablauf">
                  <FormattedMessage id="SellingInfoPage.cta.viewVideoProcess" />
                </a>
              </div>
            </div>
            <div className={css.heroVisual}>
              <div
                className={css.listingPreview}
                aria-label={intl.formatMessage({ id: 'SellingInfoPage.heroPanel.ariaLabel' })}
              >
                <div className={css.previewHeader}>
                  <span className={css.previewStatus}>
                    <FormattedMessage id="SellingInfoPage.heroPreview.status" />
                  </span>
                  <span className={css.previewFee}>
                    <FormattedMessage id="SellingInfoPage.heroPreview.fee" />
                  </span>
                </div>
                <div className={css.previewListing}>
                  <div className={css.previewImageWrap}>
                    <img
                      className={css.previewImage}
                      src={PREVIEW_IMAGE_SRC}
                      alt={intl.formatMessage({ id: 'SellingInfoPage.heroPreview.imageAlt' })}
                      decoding="async"
                      width="240"
                      height="240"
                    />
                  </div>
                  <div className={css.previewDetails}>
                    <p className={css.previewLabel}>
                      <FormattedMessage id="SellingInfoPage.heroPreview.modelLabel" />
                    </p>
                    <p className={css.previewTitle}>
                      <FormattedMessage id="SellingInfoPage.heroPreview.modelName" />
                    </p>
                    <div className={css.previewMetaGrid}>
                      <span className={css.previewMetaItem}>
                        <strong className={css.previewMetaLabel}>
                          <FormattedMessage id="SellingInfoPage.heroPreview.conditionLabel" />
                        </strong>
                        <FormattedMessage id="SellingInfoPage.heroPreview.conditionValue" />
                      </span>
                      <span className={css.previewMetaItem}>
                        <strong className={css.previewMetaLabel}>
                          <FormattedMessage id="SellingInfoPage.heroPreview.priceLabel" />
                        </strong>
                        <FormattedMessage id="SellingInfoPage.heroPreview.priceValue" />
                      </span>
                    </div>
                  </div>
                </div>
                <div className={css.previewPhotoStrip}>
                  <span className={css.previewPhoto} />
                  <span className={css.previewPhoto} />
                  <span className={css.previewPhoto} />
                  <span className={css.previewPhotoCount}>
                    <FormattedMessage id="SellingInfoPage.heroPreview.photos" />
                  </span>
                </div>
                <div className={css.previewSteps}>
                  <p className={css.panelKicker}>
                    <FormattedMessage id="SellingInfoPage.heroPanel.title" />
                  </p>
                  <ol className={css.heroStepList}>
                    {heroOfferSteps.map((stepId, index) => (
                      <li key={stepId} className={css.heroStep}>
                        <span className={css.heroStepNumber}>{index + 1}</span>
                        <span className={css.heroStepText}>
                          <FormattedMessage id={stepId} />
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
              <p className={css.heroPanelComplete}>
                <FormattedMessage id="SellingInfoPage.heroPanel.complete" />
              </p>
            </div>
          </section>

          <section className={css.trustSection}>
            <div className={css.trustContent}>
              <div className={css.sectionIntro}>
                <SectionHeading id="SellingInfoPage.why.title" />
                <p className={css.sectionText}>
                  <FormattedMessage id="SellingInfoPage.why.text" />
                </p>
              </div>
              <ul className={css.benefitList}>
                {benefitIds.map(id => (
                  <li key={id} className={css.benefitItem}>
                    <IconCheckmark className={css.checkmark} size="small" />
                    <FormattedMessage id={id} />
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className={css.processSection} id="ablauf">
            <div className={css.sectionIntro}>
              <p className={css.sectionEyebrow}>
                <FormattedMessage id="SellingInfoPage.process.eyebrow" />
              </p>
              <SectionHeading id="SellingInfoPage.process.title" />
            </div>
            <div className={css.processVideo}>
              <VideoCard
                className={css.processVideoCard}
                video={SELLER_VIDEO}
                active={activeVideoId === SELLER_VIDEO.youtubeId}
                onPlay={onPlay}
                onClose={onClose}
              />
            </div>
            <ol className={css.steps}>
              {saleSteps.map((step, index) => (
                <li key={step.title} className={css.step}>
                  <span className={css.stepNumber}>{index + 1}</span>
                  <div className={css.stepContent}>
                    <h3 className={css.stepTitle}>
                      <FormattedMessage id={step.title} />
                    </h3>
                    <p className={css.stepText}>
                      <FormattedMessage id={step.text} />
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <section className={css.moneySection}>
            <div className={css.moneyIntro}>
              <p className={css.sectionEyebrow}>
                <FormattedMessage id="SellingInfoPage.money.eyebrow" />
              </p>
              <SectionHeading id="SellingInfoPage.money.title" />
              <p className={css.sectionText}>
                <FormattedMessage id="SellingInfoPage.money.text" />
              </p>
            </div>
            <div className={css.moneyGrid}>
              <div className={css.moneyCopy}>
                <div className={css.moneyBlock}>
                  <h3 className={css.moneyHeading}>
                    <FormattedMessage id="SellingInfoPage.fees.title" />
                  </h3>
                  <p className={css.sectionText}>
                    <FormattedMessage id="SellingInfoPage.fees.text" />
                  </p>
                  <p className={css.sectionText}>
                    <FormattedMessage id="SellingInfoPage.fees.example" />
                  </p>
                </div>
                <div className={css.moneyBlock}>
                  <h3 className={css.moneyHeading}>
                    <FormattedMessage id="SellingInfoPage.stripe.title" />
                  </h3>
                  <p className={css.sectionText}>
                    <FormattedMessage id="SellingInfoPage.stripe.text" />
                  </p>
                  <p className={css.sectionText}>
                    <FormattedMessage id="SellingInfoPage.stripe.trust" />
                  </p>
                </div>
              </div>
            </div>
            <ol className={css.paymentFlow}>
              {paymentFlowItems.map((item, index) => (
                <li key={item.title} className={css.paymentFlowItem}>
                  <span className={css.paymentFlowNumber}>{index + 1}</span>
                  <strong className={css.paymentFlowTitle}>
                    <FormattedMessage id={item.title} />
                  </strong>
                  <span className={css.paymentFlowText}>
                    <FormattedMessage id={item.text} />
                  </span>
                </li>
              ))}
            </ol>
          </section>

          <section className={css.assuranceSection}>
            <div className={css.reputationBlock}>
              <div className={css.sectionIntro}>
                <p className={css.sectionEyebrow}>
                  <FormattedMessage id="SellingInfoPage.reputation.eyebrow" />
                </p>
                <SectionHeading id="SellingInfoPage.reputation.title" />
                <p className={css.sectionText}>
                  <FormattedMessage id="SellingInfoPage.reputation.text" />
                </p>
              </div>
              <ul className={css.reputationList}>
                {reputationPointIds.map(id => (
                  <li key={id} className={css.reputationItem}>
                    <IconCheckmark className={css.checkmark} size="small" />
                    <FormattedMessage id={id} />
                  </li>
                ))}
              </ul>
            </div>
            <div className={css.problemBlock}>
              <p className={css.sectionEyebrow}>
                <FormattedMessage id="SellingInfoPage.problem.eyebrow" />
              </p>
              <SectionHeading id="SellingInfoPage.problem.title" />
              <p className={css.sectionText}>
                <FormattedMessage id="SellingInfoPage.problem.text" />
              </p>
            </div>
          </section>

          <section className={css.section} id="faq">
            <div className={css.sectionIntro}>
              <SectionHeading id="SellingInfoPage.faq.title" />
            </div>
            <div className={css.faqList}>
              {faqItems.map(item => (
                <details key={item.question} className={css.faqItem}>
                  <summary className={css.faqQuestion}>
                    <span>
                      <FormattedMessage id={item.question} />
                    </span>
                    <span className={css.faqIcon}>+</span>
                  </summary>
                  <p className={css.faqAnswer}>
                    <FormattedMessage id={item.answer} />
                  </p>
                </details>
              ))}
            </div>
          </section>

          <section className={css.finalCta}>
            <H2 rootClassName={css.finalCtaTitle}>
              <FormattedMessage id="SellingInfoPage.finalCta.title" />
            </H2>
            <p className={css.finalCtaText}>
              <FormattedMessage id="SellingInfoPage.finalCta.text" />
            </p>
            <StartSellingCTA className={css.primaryButton} ctaLocation="selling_info_final">
              <FormattedMessage id="SellingInfoPage.cta.selectModel" />
            </StartSellingCTA>
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const SellingInfoPage = compose(connect(mapStateToProps))(SellingInfoPageComponent);

export default SellingInfoPage;
